import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-client'
import { useAuth } from '@/features/auth/auth-provider'
import {
  acceptInvite,
  acceptInviteToast,
  clearInviteToken,
  fetchPublicInvite,
  saveInviteToken,
  type PublicInvite,
} from '@/lib/invite'
import { ROLE_LABELS } from '@/pages/settings/team'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

export function InvitePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [invite, setInvite] = useState<PublicInvite | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (token) saveInviteToken(token)
    fetchPublicInvite(token)
      .then((data) => {
        if (!cancelled) setInvite(data)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function handleAccept() {
    if (!token || !invite) return
    setAccepting(true)
    setAcceptError(null)
    try {
      const result = await acceptInvite(token)
      clearInviteToken()
      toast.success(acceptInviteToast(result.status))
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.status === 403 && err.code === 'email_mismatch') {
        setAcceptError(`Diese Einladung gehört zu einer anderen E-Mail-Adresse. Melde dich mit ${invite.email} an.`)
      } else if (err instanceof ApiError && err.status === 404) {
        setAcceptError('Einladung nicht gefunden oder abgelaufen.')
      } else {
        setAcceptError(err instanceof Error ? err.message : 'Einladung konnte nicht angenommen werden.')
      }
    } finally {
      setAccepting(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-8 text-center">
          <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">MindKeep</h1>
          <p className="mt-1 text-sm text-muted-foreground">KI-Wissensbasis für dein Unternehmen</p>
        </div>

        <Card>
          {loading ? (
            <>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-56" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </>
          ) : loadError || !invite ? (
            <>
              <CardHeader>
                <CardTitle>Einladung nicht gefunden oder abgelaufen.</CardTitle>
                <CardDescription>Bitte die einladende Person um eine neue Einladung.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Zum Login</Link>
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="pb-4">
                <CardTitle>Einladung zu „<span className="break-words">{invite.workspace_name}</span>“</CardTitle>
                <CardDescription>Du wurdest zu diesem Workspace eingeladen.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <dl className="space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-muted-foreground">E-Mail</dt>
                    <dd className="min-w-0 break-words text-right font-medium">{invite.email}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-muted-foreground">Rolle</dt>
                    <dd className="font-medium">{ROLE_LABELS[invite.role]}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-muted-foreground">Gültig bis</dt>
                    <dd className="font-medium">{new Date(invite.expires_at).toLocaleDateString('de-CH')}</dd>
                  </div>
                </dl>

                {acceptError ? (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {acceptError}
                  </p>
                ) : null}

                {authLoading ? null : isAuthenticated ? (
                  <Button
                    type="button"
                    data-testid="invite-accept"
                    className="w-full"
                    disabled={accepting}
                    onClick={() => void handleAccept()}
                  >
                    {accepting ? 'Wird angenommen…' : 'Einladung annehmen'}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button asChild className="w-full">
                      <Link to={`/auth/signup?invite=${encodeURIComponent(token)}`}>Konto erstellen</Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full">
                      <Link to={`/login?invite=${encodeURIComponent(token)}`}>Anmelden</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </main>
  )
}
